// widget/src/hooks/useOpenAIVoice.ts
import { useState, useRef, useCallback, useEffect } from 'react';

type VoiceStatus = 'idle' | 'connecting' | 'connected' | 'speaking' | 'error' | 'timeout';

interface UseOpenAIVoiceOptions {
  supabaseUrl: string;
  supabaseAnonKey: string;
  userEmail: string | null;
}

export interface UseOpenAIVoiceReturn {
  status: VoiceStatus;
  isConnected: boolean;
  isMicOn: boolean;
  audioLevel: number;
  transcript: string;
  hasSavedLead: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  toggleMic: () => void;
}

export function useOpenAIVoice({
  supabaseUrl,
  supabaseAnonKey,
  userEmail
}: UseOpenAIVoiceOptions): UseOpenAIVoiceReturn {
  const [status, setStatusState] = useState<VoiceStatus>('idle');

  // Wrapper to keep ref in sync with state
  const setStatus = useCallback((newStatus: VoiceStatus) => {
    statusRef.current = newStatus;
    setStatusState(newStatus);
  }, []);

  const [isMicOn, setIsMicOn] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const [transcript, setTranscript] = useState('');

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const isConnectingRef = useRef(false); // Use ref to prevent duplicate calls
  const statusRef = useRef<VoiceStatus>('idle'); // Ref for synchronous access in callbacks
  const startTimeRef = useRef<number | null>(null); // Track call start time
  const inactivityTimerRef = useRef<number | null>(null); // Track silence for auto-disconnect

  // Clean up on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  // Audio level monitoring
  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const checkLevel = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);

      animationFrameRef.current = requestAnimationFrame(checkLevel);
    };

    checkLevel();
  }, []);

  const connect = useCallback(async () => {
    // Use ref to prevent duplicate calls (avoids stale closure issues)
    if (isConnectingRef.current || pcRef.current) {
      console.log('useOpenAIVoice: Already connecting or connected, skipping');
      return;
    }

    isConnectingRef.current = true;
    console.log('useOpenAIVoice: Starting connection...');
    setStatus('connecting');
    setTranscript('');

    try {
      // 1. Get ephemeral token from our edge function
      const tokenResponse = await fetch(`${supabaseUrl}/functions/v1/realtime-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ userEmail }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get session token');
      }

      const data = await tokenResponse.json();
      const token = data.token;

      if (!token) {
        throw new Error('No token received');
      }

      // 2. Create peer connection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // 3. Set up audio playback
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;

      pc.oniceconnectionstatechange = () => {
        console.log('useOpenAIVoice: ICE state:', pc.iceConnectionState);
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          setStatus('error');
        }
      };

      pc.onconnectionstatechange = () => {
        console.log('useOpenAIVoice: Connection state:', pc.connectionState);
        if (pc.connectionState === 'failed') {
          setStatus('error');
        }
      };

      pc.ontrack = (e) => {
        console.log('useOpenAIVoice: Received remote track');
        audioEl.srcObject = e.streams[0];
      };

      // 4. Get microphone access and add to peer connection
      console.log('useOpenAIVoice: Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      streamRef.current = stream;

      // Set up audio analysis for visualizer
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      // Add track to peer connection
      stream.getTracks().forEach(track => {
        console.log('useOpenAIVoice: Adding track:', track.kind);
        pc.addTrack(track, stream);
      });

      // 5. Create data channel for events
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onopen = () => {
        console.log('useOpenAIVoice: Data channel open');

        // Start call timer
        startTimeRef.current = Date.now();
        resetInactivityTimer();

        // Inject user email into the session instructions so Nova knows it
        if (userEmail) {
          console.log('useOpenAIVoice: User email verified for session:', userEmail);
          // NOTE: We now handle email injection on the SERVER side in realtime-token/index.ts
          // to avoid overwriting the entire instructions block with a client-side update.
        }

        // Note: We do NOT send a greeting trigger here anymore.
        // The server VAD or system prompt instruction is sufficient.
        // Sending one causes a "double hello" race condition.
      };

      dc.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          console.log(`useOpenAIVoice: Event ${event.type}:`, event);
          handleServerEvent(event);
        } catch (err) {
          console.error('useOpenAIVoice: Failed to parse server event:', err);
        }
      };

      // 6. Create and set local description
      console.log('useOpenAIVoice: Creating offer...');
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // 7. Send offer to OpenAI and get answer
      console.log('useOpenAIVoice: Sending offer to OpenAI...');
      const sdpResponse = await fetch('https://api.openai.com/v1/realtime?model=gpt-realtime-mini', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      });

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error('useOpenAIVoice: OpenAI SDP error:', errorText);
        throw new Error('Failed to connect to OpenAI');
      }

      console.log('useOpenAIVoice: Received answer from OpenAI');
      const answerSdp = await sdpResponse.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      console.log('useOpenAIVoice: Connection established');
      setStatus('connected');
      monitorAudioLevel();

    } catch (err) {
      console.error('useOpenAIVoice: Connection error:', err);
      isConnectingRef.current = false;
      setStatus('error');
      disconnect();
    }
  }, [supabaseUrl, supabaseAnonKey, userEmail, monitorAudioLevel]);

  const hasSavedLeadRef = useRef(false);

  const handleFunctionCall = useCallback(async (event: any) => {
    const { name, call_id, arguments: argsString } = event;
    console.log(`useOpenAIVoice: Running tool ${name}...`, argsString);

    if (name === 'save_lead') {
      hasSavedLeadRef.current = true;
    }

    if (name === 'end_call') {
      console.log('useOpenAIVoice: Received end_call signal from AI');
      // Give the AI a moment to finish saying "Goodbye" (8 seconds)
      setTimeout(() => {
        console.log('useOpenAIVoice: Triggering completion flow');
        setStatus('timeout');
      }, 8000);
      return;
    }

    // Calculate duration since call started
    let durationString = '';
    if (startTimeRef.current) {
      const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      durationString = `${minutes}m ${remainingSeconds}s`;
    }

    try {
      const args = JSON.parse(argsString);

      // Call Supabase edge function to handle the actual logic
      const response = await fetch(`${supabaseUrl}/functions/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          tool_call: { name, args },
          userEmail,
          duration: durationString // Pass call duration
        }),
      });

      let result;
      if (response.ok) {
        result = await response.json();
      } else {
        result = { error: 'Failed to execute tool' };
      }

      console.log(`useOpenAIVoice: Tool ${name} finished:`, result);

      if (dcRef.current?.readyState === 'open') {
        dcRef.current.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: call_id,
            output: JSON.stringify(result),
          }
        }));

        dcRef.current.send(JSON.stringify({ type: 'response.create' }));
      }
    } catch (err) {
      console.error(`useOpenAIVoice: Tool ${name} failed:`, err);
    }
  }, [supabaseUrl, supabaseAnonKey, userEmail]);

  const handleServerEvent = useCallback((event: any) => {
    console.log(`useOpenAIVoice: Received event ${event.type}:`, event);

    switch (event.type) {
      case 'response.audio.delta':
        setStatus('speaking');
        clearInactivityTimer(); // AI is speaking, pause timeout
        break;

      case 'response.audio.done':
      case 'output_audio_buffer.stopped':
        console.log('useOpenAIVoice: Audio output finished');
        setStatus('connected');
        resetInactivityTimer(); // AI finished, start timeout
        // We don't append to transcript here because we do it progressively in response.text.delta
        // But for clarity in the final log, we might want to ensure newlines.
        setTranscript(prev => prev.endsWith('\n') ? prev : prev + '\n');
        break;

      case 'response.text.delta':
        if (event.delta) {
          // If this is the start of a new AI turn (previous char was newline), add prefix
          setTranscript(prev => {
            if (prev === '' || prev.endsWith('\n')) {
              return prev + 'AI: ' + event.delta;
            }
            return prev + event.delta;
          });
        }
        break;

      case 'input_audio_buffer.speech_started':
        console.log('useOpenAIVoice: 🟢 Server detected user speech starting');
        clearInactivityTimer(); // User talked, cancel timeout
        break;

      case 'input_audio_buffer.speech_stopped':
        console.log('useOpenAIVoice: 🔴 Server detected user speech stopped');
        // Only restart timer if AI is NOT currently speaking (prevent VAD noise triggering timeout)
        if (statusRef.current !== 'speaking') {
          resetInactivityTimer();
        }
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          console.log('User said (transcribed):', event.transcript);
          setTranscript(prev => prev + `User: ${event.transcript}\n`);
        }
        break;

      case 'response.function_call_arguments.done': {
        handleFunctionCall(event);
        break;
      }

      case 'error':
        console.error('useOpenAIVoice: Server reported error:', event.error);
        break;
    }
  }, [handleFunctionCall]);

  const disconnect = useCallback((targetStatus?: VoiceStatus) => {
    console.log('useOpenAIVoice: Disconnecting...');

    // Reset connecting flag
    isConnectingRef.current = false;

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Close data channel
    if (dcRef.current) {
      dcRef.current.close();
      dcRef.current = null;
    }

    // Close peer connection
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    clearInactivityTimer();
    setStatus(targetStatus || 'idle');
    setAudioLevel(0);
    setIsMicOn(true);
    startTimeRef.current = null;
  }, []);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    // 60-second inactivity timeout (Standard wait time)
    inactivityTimerRef.current = window.setTimeout(() => {
      console.log('useOpenAIVoice: Inactivity timeout reached (60s).');
      disconnect('timeout');
    }, 60000);
  }, [clearInactivityTimer, disconnect]);

  const toggleMic = useCallback(() => {
    if (streamRef.current) {
      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMicOn(audioTrack.enabled);
      }
    }
  }, []);

  return {
    status,
    isConnected: status === 'connected' || status === 'speaking',
    isMicOn,
    audioLevel,
    transcript,
    hasSavedLead: hasSavedLeadRef.current,
    connect,
    disconnect,
    toggleMic,
  };
}

export default useOpenAIVoice;
